import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store';
import { useOrgStore } from '../../store/org';
import { ORG_ROLE_LABEL } from '../../auth/capabilities';
import { VpState } from '../../components/VelopulseShell';
import useLoad from '../../utils/useLoad';
import { vpApi, errorMessage, INVITE_STATUS_LABEL } from '../../api/velopulse';

const emptyInvite = { email: '', name: '', org_role: 'member' };
const OPEN_INVITE = new Set(['draft', 'sent', 'failed', 'pending']);

export default function OrgAdminMembersPage() {
  const currentUser = useAuthStore((s) => s.user);
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inviteQ, setInviteQ] = useState('');
  const [inviteStatus, setInviteStatus] = useState('open');
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(emptyInvite);
  const [saving, setSaving] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState('');

  const membersLoad = useLoad(
    () => vpApi.listOrgMembers({ q, org_role: roleFilter, status: statusFilter }),
    [q, roleFilter, statusFilter],
  );
  const invitesLoad = useLoad(
    () => vpApi.listOrgInvites({ q: inviteQ, status: inviteStatus }),
    [inviteQ, inviteStatus],
  );

  const members = Array.isArray(membersLoad.data) ? membersLoad.data : [];
  const invites = Array.isArray(invitesLoad.data) ? invitesLoad.data : [];

  const reloadAll = () => { membersLoad.reload(); invitesLoad.reload(); };

  const openInvite = () => {
    setForm(emptyInvite);
    setCopiedUrl('');
    setShowInvite(true);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    if (!email) { toast.error('請輸入 Email'); return; }
    const roleName = ORG_ROLE_LABEL[form.org_role] || form.org_role;
    const ok = window.confirm(
      `確定邀請「${email}」以「${roleName}」加入「${currentOrg?.name}」？\n\n確認後才會建立邀請並寄出信件。`
    );
    if (!ok) return;
    setSaving(true);
    setCopiedUrl('');
    try {
      const draft = await vpApi.createOrgInvite({
        email, name: form.name.trim(), org_role: form.org_role,
      });
      const sent = await vpApi.sendOrgInvite(draft.id);
      if (sent.send === 'sent') {
        toast.success(sent.stubbed ? '邀請已建立（郵件未設定，請複製連結）' : '邀請已寄出');
        if (sent.invite?.invite_url) setCopiedUrl(sent.invite.invite_url);
        else setShowInvite(false);
      } else {
        toast.error(sent.error || '邀請信寄送失敗');
      }
      reloadAll();
    } catch (err) {
      toast.error(errorMessage(err, '邀請失敗，請重試'));
    } finally {
      setSaving(false);
    }
  };

  const handleRole = async (m, org_role) => {
    if (org_role === m.org_role) return;
    if (m.role === 'super_admin') { toast.error('無法變更超級管理員的公司身分'); return; }
    const ok = window.confirm(
      `確定將「${m.name}」在「${currentOrg?.name}」的角色改為「${ORG_ROLE_LABEL[org_role] || org_role}」？`
    );
    if (!ok) return;
    try {
      await vpApi.updateOrgMember(m.id, { org_role });
      toast.success('公司角色已更新');
      membersLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法更新角色'));
    }
  };

  const handleToggle = async (m) => {
    if (m.id === currentUser?.id) { toast.error('無法停用自己的帳號'); return; }
    if (m.role === 'super_admin') { toast.error('無法變更超級管理員的公司身分'); return; }
    const next = m.is_active === false;
    const ok = window.confirm(next
      ? `確定恢復「${m.name}」在「${currentOrg?.name}」的存取？`
      : `確定停止「${m.name}」在「${currentOrg?.name}」的存取？\n\n只停止他在本公司的權限，不會停用其其他公司帳號，也不會停用全域登入。`);
    if (!ok) return;
    try {
      await vpApi.setOrgMemberStatus(m.id, next);
      toast.success(next ? '已恢復本公司存取' : '已停止本公司存取');
      membersLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '操作失敗'));
    }
  };

  const handleResend = async (inv) => {
    const ok = window.confirm(`確定重寄邀請給「${inv.email}」？舊的邀請連結將失效。`);
    if (!ok) return;
    try {
      const sent = await vpApi.resendOrgInvite(inv.id);
      toast.success(sent.stubbed ? '已重寄（郵件未設定，請複製連結）' : '邀請已重寄');
      if (sent.invite?.invite_url) {
        try { await navigator.clipboard.writeText(sent.invite.invite_url); toast.success('連結已複製'); } catch { /* ignore */ }
      }
      invitesLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '重寄失敗'));
    }
  };

  const handleRevoke = async (inv) => {
    const ok = window.confirm(`確定撤銷「${inv.email}」的邀請？對方將無法再用此連結加入。`);
    if (!ok) return;
    try {
      await vpApi.revokeOrgInvite(inv.id);
      toast.success('邀請已撤銷');
      invitesLoad.reload();
    } catch (err) {
      toast.error(errorMessage(err, '撤銷失敗'));
    }
  };

  if (membersLoad.loading && invitesLoad.loading) return <div className="vp-content"><VpState loading /></div>;

  return (
    <div className="vp-content" style={{ maxWidth: 1040 }}>
      <div className="vp-hello">
        <div>
          <h1>成員</h1>
          <p>範圍：{currentOrg?.name || '目前公司'}。名單與邀請都只屬於這家公司。</p>
        </div>
        <div className="vp-actions">
          <button type="button" className="vp-btn primary" onClick={openInvite}>邀請成員</button>
        </div>
      </div>

      <div className="vp-bar">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜尋姓名或 Email" aria-label="搜尋成員" />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="角色篩選">
          <option value="">全部角色</option>
          <option value="org_admin">公司管理員</option>
          <option value="team_manager">團隊主管</option>
          <option value="member">一般成員</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="狀態篩選">
          <option value="all">全部狀態</option>
          <option value="active">啟用中</option>
          <option value="inactive">已停用（本公司）</option>
        </select>
      </div>

      {membersLoad.error ? (
        <div className="vp-card"><VpState text={errorMessage(membersLoad.error, '無法載入成員名單。')} action={{ label: '重新載入', onClick: membersLoad.reload }} /></div>
      ) : members.length === 0 ? (
        <div className="vp-card"><VpState text="沒有符合條件的成員。" action={{ label: '邀請成員', onClick: openInvite }} /></div>
      ) : (
        <div className="vp-list-card">
          <table className="vp-table">
            <thead>
              <tr>
                {['姓名', 'Email', '公司角色', '狀態', '操作'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const locked = m.role === 'super_admin' || m.id === currentUser?.id;
                return (
                  <tr key={m.id}>
                    <td>{m.name}{m.id === currentUser?.id ? '（我）' : ''}{m.role === 'super_admin' ? ' · 平台' : ''}</td>
                    <td>{m.email}</td>
                    <td>
                      <select
                        className="vp-input"
                        style={{ minWidth: 140, padding: '6px 8px' }}
                        value={m.org_role || 'member'}
                        disabled={m.role === 'super_admin'}
                        onChange={(e) => handleRole(m, e.target.value)}
                      >
                        <option value="member">一般成員</option>
                        <option value="team_manager">團隊主管</option>
                        <option value="org_admin">公司管理員</option>
                      </select>
                    </td>
                    <td>{m.is_active === false ? '已停用（本公司）' : '啟用中'}</td>
                    <td>
                      <button
                        type="button"
                        className={`vp-btn${locked && m.id === currentUser?.id ? '' : ' danger'}`}
                        disabled={locked}
                        onClick={() => handleToggle(m)}
                      >
                        {m.is_active === false ? '恢復' : '停用'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="vp-hello" style={{ marginTop: 28 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>邀請</h2>
          <p>草稿需確認後才寄出。重寄會讓舊連結失效。</p>
        </div>
      </div>
      <div className="vp-bar">
        <input value={inviteQ} onChange={(e) => setInviteQ(e.target.value)} placeholder="搜尋邀請 Email" aria-label="搜尋邀請" />
        <select value={inviteStatus} onChange={(e) => setInviteStatus(e.target.value)} aria-label="邀請狀態">
          <option value="open">進行中</option>
          <option value="pending">待接受</option>
          <option value="draft">草稿</option>
          <option value="failed">寄送失敗</option>
          <option value="accepted">已接受</option>
          <option value="expired">已過期</option>
          <option value="revoked">已撤銷</option>
        </select>
      </div>
      {invitesLoad.error ? (
        <div className="vp-card"><VpState text={errorMessage(invitesLoad.error, '無法載入邀請。')} action={{ label: '重新載入', onClick: invitesLoad.reload }} /></div>
      ) : invites.length === 0 ? (
        <div className="vp-card"><p className="vp-muted" style={{ margin: 0 }}>沒有符合條件的邀請。</p></div>
      ) : (
        <div className="vp-list-card">
          <table className="vp-table">
            <thead>
              <tr>
                {['Email', '角色', '狀態', '到期', '操作'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.email}{inv.name ? `（${inv.name}）` : ''}</td>
                  <td>{inv.org_role_label || ORG_ROLE_LABEL[inv.org_role]}</td>
                  <td>
                    <span className={`vp-pill ${inv.status}`}>{INVITE_STATUS_LABEL[inv.status] || inv.status}</span>
                    {inv.send_status === 'failed' && inv.status !== 'failed' ? ' · 上次寄送失敗' : ''}
                  </td>
                  <td>{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString('zh-TW') : '—'}</td>
                  <td>
                    {OPEN_INVITE.has(inv.status) && (
                      <>
                        <button type="button" className="vp-btn" onClick={() => handleResend(inv)}>
                          {inv.status === 'draft' ? '寄出' : '重寄'}
                        </button>
                        <button type="button" className="vp-btn danger" onClick={() => handleRevoke(inv)}>撤銷</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <div className="vp-modal-mask" onClick={(e) => { if (e.target === e.currentTarget) setShowInvite(false); }}>
          <div className="vp-modal" role="dialog" aria-labelledby="invite-form-title">
            <div className="vp-modal-head">
              <h2 id="invite-form-title">邀請加入本公司</h2>
              <button type="button" className="vp-modal-x" onClick={() => setShowInvite(false)} aria-label="關閉">✕</button>
            </div>
            <p className="vp-muted" style={{ marginTop: 0 }}>邀請綁定「{currentOrg?.name}」與這個 Email。對方若已有帳號，接受後只會加入本公司，不會重設密碼。</p>
            <form onSubmit={handleInvite}>
              <label className="vp-label">Email *
                <input className="vp-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </label>
              <label className="vp-label">顯示名稱（選填）
                <input className="vp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="vp-label">公司角色
                <select className="vp-input" value={form.org_role} onChange={(e) => setForm({ ...form, org_role: e.target.value })}>
                  <option value="member">一般成員</option>
                  <option value="team_manager">團隊主管</option>
                  <option value="org_admin">公司管理員</option>
                </select>
              </label>
              {copiedUrl && (
                <div className="vp-warnbox">
                  郵件服務未設定，請把這條連結傳給對方：
                  <div style={{ wordBreak: 'break-all', marginTop: 8 }}>{copiedUrl}</div>
                </div>
              )}
              <div className="vp-wiz-actions">
                <button type="button" className="vp-btn" onClick={() => setShowInvite(false)}>取消</button>
                <button type="submit" className="vp-btn primary" disabled={saving}>{saving ? '處理中…' : '確認並寄出'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
